import React, { useEffect, useState, useRef, lazy } from 'react';
import { motion } from 'framer-motion';
import { imageCache, optimizeImageUrl } from '../lib/imageCache';
interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  thumbnail?: string;
  onLoad?: () => void;
}
export function LazyImage({
  src,
  alt,
  className = '',
  thumbnail,
  onLoad
}: LazyImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [imageSrc, setImageSrc] = useState<string>('');
  const imgRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    if (!imgRef.current) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      });
    }, {
      rootMargin: '100px' // Start loading 100px before image enters viewport
    });
    observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (isInView && src) {
      // Optimize URL and set for loading
      const optimizedUrl = optimizeImageUrl(src);
      setImageSrc(optimizedUrl);
      // Preload image in cache for next time
      imageCache.preload(optimizedUrl);
    }
  }, [isInView, src]);
  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };
  return <div ref={imgRef} className={`relative overflow-hidden ${className}`} data-id="element-118">
      {/* Thumbnail/placeholder */}
      {!isLoaded && <motion.div initial={{
      opacity: 1
    }} animate={{
      opacity: isLoaded ? 0 : 1
    }} className="absolute inset-0 bg-neutral-900" data-id="element-119">
          {thumbnail ? <img src={thumbnail} alt={alt} className="w-full h-full object-cover blur-sm scale-110" data-id="element-120" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-121">
              <motion.div animate={{
          opacity: [0.3, 0.6, 0.3]
        }} transition={{
          duration: 1.5,
          repeat: Infinity
        }} className="w-12 h-12 border-2 border-white/20 border-t-white/60 rounded-full" style={{
          animation: 'spin 1s linear infinite'
        }} data-id="element-122" />
            </div>}
        </motion.div>}

      {/* Actual image */}
      {imageSrc && <motion.img src={imageSrc} alt={alt} onLoad={handleLoad} initial={{
      opacity: 0
    }} animate={{
      opacity: isLoaded ? 1 : 0
    }} transition={{
      duration: 0.3
    }} className={`w-full h-full object-cover ${className}`} loading="lazy" decoding="async" data-id="element-123" />}
    </div>;
}